import { Drawer, Form, message as antdMessage } from 'antd';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FirstStep } from './form/FirstStep';
import { SecondStep } from './form/SecondStep';
import { LastStep } from './form/LastStep';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';
import { usersFormDataFormat } from '../../routes/usersShows/supervisions/utils/usersFormDataFormat';
import { adminFormDataFormat } from './utils/formDataFormat';
import { InfosStep } from './form/InfosStep';

export const ReviewDrawer = ({
  open,
  setOpen,
  supervision,
  setForceRefresh,
  customInstanceForm,
  showReviewDrawer,
  setSupervision,
  editReviewDrawer,
  reviewData,
  forceRefresh,
  supervisions,
  questionnaireType,
  setQuestionnaireType
}) => {
  const { dispatchAPI, user } = useAuthContext();
  const [form] = Form.useForm();
  const { message } = useErrorMessage();
  const { t } = useTranslation();

  const [current, setCurrent] = useState(user.role === 'admins:ADMIN' ? 0 : 1);
  const [infosStep, setInfosStep] = useState({});
  const [firstStep, setFirstStep] = useState({});
  const [secondStep, setSecondStep] = useState({});
  const [lastStep, setLastStep] = useState({});
  const [ratingDate, setRatingDate] = useState({});

  // "Show" without "edit" is the read-only view: the answers are displayed
  // but nothing can be changed nor submitted (see the effect below).
  const readOnly = showReviewDrawer && !editReviewDrawer;

  const handleSubmit = async () => {
    try {
      const body = ['users'].includes(user.role.split(':')[0])
        ? usersFormDataFormat({
            firstStep,
            secondStep,
            lastStep,
            supervision,
            user,
            userRole: user.role
          })
        : adminFormDataFormat({
            infosStep,
            firstStep,
            secondStep,
            lastStep,
            supervision,
            user,
            ratingDate,
            userRole: questionnaireType
          });

      if (!body.receiver?.id?.length) {
        antdMessage.error(
          t('owners.users_shows.review_drawer.form.messages.missing_receiver')
        );
        return;
      }

      const action = editReviewDrawer ? 'PATCH' : 'POST';
      const url = editReviewDrawer ? `/ratings/${reviewData._id}` : '/ratings';

      await dispatchAPI(action, { url, body });

      antdMessage.success(
        t('owners.users_shows.review_drawer.form.messages.success')
      );

      setCurrent(0);
      setForceRefresh(!forceRefresh);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      if (Object.keys(lastStep).length && !readOnly) await handleSubmit();
      if (!showReviewDrawer) {
        if (customInstanceForm) {
          customInstanceForm.resetFields();
        } else {
          form.resetFields();
        }
      }
      setOpen(false);
      setCurrent(0);
    })();
  }, [lastStep]);

  const forms = [
    ...(user.role === 'admins:ADMIN'
      ? [
          <InfosStep
            key="infos-step"
            form={customInstanceForm || form}
            setCurrent={setCurrent}
            setOpen={setOpen}
            user={user}
            infosStep={infosStep}
            setInfosStep={setInfosStep}
            supervision={supervision}
            supervisions={supervisions}
            setRatingDate={setRatingDate}
            showReviewDrawer={showReviewDrawer}
            setSupervision={setSupervision}
            setQuestionnaireType={setQuestionnaireType}
            questionnaireType={questionnaireType}
          />
        ]
      : []),
    <FirstStep
      key="first-step"
      form={customInstanceForm || form}
      firstStep={firstStep}
      setFirstStep={setFirstStep}
      setCurrent={setCurrent}
      setOpen={setOpen}
      user={user}
      supervision={supervision}
      questionnaireType={questionnaireType}
      readOnly={readOnly}
    />,
    <SecondStep
      key="second-step"
      form={customInstanceForm || form}
      secondStep={secondStep}
      setSecondStep={setSecondStep}
      setCurrent={setCurrent}
      user={user}
      questionnaireType={questionnaireType}
      readOnly={readOnly}
    />,
    <LastStep
      key="last-step"
      form={customInstanceForm || form}
      lastStep={lastStep}
      setLastStep={setLastStep}
      setCurrent={setCurrent}
      user={user}
      readOnly={readOnly}
    />
  ];

  return (
    <Drawer
      width={600}
      onClose={() => {
        setOpen(false);
        setCurrent(0);
      }}
      open={open}
      title={t('owners.users_shows.review_drawer.form.title')}
    >
      {forms[current]}
    </Drawer>
  );
};

ReviewDrawer.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  supervision: PropTypes.shape({
    _id: PropTypes.string,
    pensioners: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  setForceRefresh: PropTypes.func,
  customInstanceForm: PropTypes.shape({
    resetFields: PropTypes.func
  }),
  showReviewDrawer: PropTypes.bool,
  setSupervision: PropTypes.func,
  editReviewDrawer: PropTypes.bool,
  reviewData: PropTypes.shape({
    _id: PropTypes.string
  }),
  forceRefresh: PropTypes.bool,
  supervisions: PropTypes.arrayOf(PropTypes.shape({})),
  questionnaireType: PropTypes.string,
  setQuestionnaireType: PropTypes.func
};

ReviewDrawer.defaultProps = {
  customInstanceForm: null,
  showReviewDrawer: false,
  editReviewDrawer: false,
  reviewData: {},
  forceRefresh: false,
  open: false,
  setOpen: () => {},
  supervision: {},
  setForceRefresh: () => {},
  setSupervision: () => {},
  supervisions: [],
  questionnaireType: '',
  setQuestionnaireType: () => {}
};
